/**
 * Pinned columns.
 *
 * A pinned cell keeps its place while the others scroll under it. The table
 * uses separate borders (with no spacing, so they look the same as collapsed
 * ones) because collapsed borders belong to the table and would slide away
 * from a pinned cell. A pinned column casts a shadow only while something is
 * scrolled under it.
 */

import type React from 'react';

/** Where a column is pinned, or null when it scrolls. */
export type Pin = {
  side: 'left' | 'right';
  /** Distance from that side: the widths of the pinned columns outside it. */
  offset: number;
  /** The innermost pinned column on its side, which casts the shadow. */
  edge: boolean;
} | null;

/** Pins for the first `pinLeft` and last `pinRight` of columns this wide. */
export function computePins(
  widths: readonly number[],
  pinLeft: number,
  pinRight: number
): Pin[] {
  const count = widths.length;
  const pins: Pin[] = widths.map(() => null);
  let left = 0;
  for (let i = 0; i < Math.min(pinLeft, count); i++) {
    pins[i] = { side: 'left', offset: left, edge: i === pinLeft - 1 };
    left += widths[i];
  }
  let right = 0;
  for (let i = count - 1; i >= Math.max(count - pinRight, 0); i--) {
    pins[i] = { side: 'right', offset: right, edge: i === count - pinRight };
    right += widths[i];
  }
  return pins;
}

/** How much of the scroller's width the pinned columns cover on each side. */
export function pinnedWidths(
  pins: readonly Pin[],
  widths: readonly number[]
): { left: number; right: number } {
  let left = 0;
  let right = 0;
  pins.forEach((pin, index) => {
    if (pin?.side === 'left') left += widths[index];
    if (pin?.side === 'right') right += widths[index];
  });
  return { left, right };
}

/**
 * The style and classes of a cell in a column `width` wide. A header cell
 * passes `background: false`: it sets its own colour.
 */
export function pinCellProps(
  pin: Pin,
  width: number,
  { background = true }: { background?: boolean } = {}
): { style: React.CSSProperties; className: string } {
  if (!pin) return { style: { width }, className: '' };
  // A strip just outside the pinned edge, the full height of the cell, so the
  // strips of consecutive rows join into one shadow. A box-shadow would fade
  // at each cell's top and bottom and show every row separately.
  const strip =
    "after:pointer-events-none after:absolute after:inset-y-0 after:w-2 after:content-['']";
  // Always there but invisible; the grid shows it by flagging its scroller
  // (data-shadow-left / -right), so scrolling never has to re-render a row.
  // The pin-edge-* classes are also targeted by src/index.css.
  const shadow =
    pin.edge &&
    (pin.side === 'left'
      ? `pin-edge-left ${strip} after:opacity-0 after:left-full after:bg-gradient-to-r after:from-black/10 after:to-transparent`
      : `pin-edge-right ${strip} after:opacity-0 after:right-full after:bg-gradient-to-l after:from-black/10 after:to-transparent`);
  // The outermost right-pinned column sits inside the strip the grid keeps
  // clear for the scroll bar; it paints that strip white beside itself, so the
  // rows scrolling under it do not show through. It reaches down over the
  // cell's bottom border too, or their row dividers show between the strips.
  const cover =
    pin.side === 'right' && pin.offset === 0
      ? "before:pointer-events-none before:absolute before:top-0 before:-bottom-px before:left-full before:w-3 before:bg-white before:content-['']"
      : '';
  return {
    style: { width, position: 'sticky', [pin.side]: pin.offset, zIndex: 10 },
    // A body cell takes on its row's opaque colour, so the cells scrolling
    // under it do not show through.
    className: `${background ? 'bg-inherit' : ''} ${shadow || ''} ${cover}`,
  };
}
